import type { ChakraProps, StyleProps, SystemStyleObject, ThemingProps } from '@chakra-ui/react';
import { Box, Flex, Tab, TabList, useColorModeValue } from '@chakra-ui/react';
import React from 'react';

import type { MenuButton, TabItem } from './types';

import { useScrollDirection } from 'lib/contexts/scrollDirection';
import useIsMobile from 'lib/hooks/useIsMobile';
import useIsSticky from 'lib/hooks/useIsSticky';
import Skeleton from 'ui/shared/chakra/Skeleton';

import AdaptiveTabsMobile from './AdaptiveTabsMobile';
import TabCounter from './TabCounter';
import TabsMenu from './TabsMenu';
import type { Props as TabsProps } from './TabsWithScroll';
import useAdaptiveTabs from './useAdaptiveTabs';
import useScrollToActiveTab from './useScrollToActiveTab';
import { menuButton } from './utils';

const hiddenItemStyles: StyleProps = {
  position: 'absolute',
  top: '-9999px',
  left: '-9999px',
  visibility: 'hidden',
};

interface TabThemeColors {
  activeBg: string;
  activeText: string;
  inactiveBg: string;
  inactiveText: string;
  primaryText: string;
  tabBorder: string;
}

interface Props extends TabsProps {
  activeTabIndex: number;
  onItemClick: (index: number) => void;
  themeProps: ThemingProps<'Tabs'>;
  separatorEnabled?: boolean;
  isLoading?: boolean;
}

function getStickyProps(
  stickyEnabled: boolean | undefined,
  isSticky: boolean,
  scrollDirection: 'up' | 'down' | null,
): StyleProps {
  if (!stickyEnabled) {
    return {};
  }

  return {
    position: 'sticky' as const,
    boxShadow: { base: isSticky ? 'md' : 'none', lg: 'none' },
    top: { base: scrollDirection === 'down' ? '0px' : '106px', lg: 0 },
    zIndex: { base: 'sticky2', lg: 'docked' },
  };
}

function resolveTabListProps(
  tabListProps: TabsProps['tabListProps'],
  isSticky: boolean,
  activeTabIndex: number,
): ChakraProps | undefined {
  if (typeof tabListProps === 'function') {
    return tabListProps({ isSticky, activeTabIndex });
  }

  return tabListProps;
}

function getSegmentedTabListProps(colors: Pick<TabThemeColors, 'inactiveBg' | 'tabBorder'>): ChakraProps {
  return {
    gap: 0,
    h: '40px',
    w: 'fit-content',
    minW: '352px',
    display: 'inline-flex',
    overflow: 'hidden',
    bgColor: colors.inactiveBg,
    border: '1px solid',
    borderColor: colors.tabBorder,
    borderRadius: '4px',
    px: 0,
    mx: 0,
    mb: 4,
  };
}

function getSegmentedTabSx(colors: TabThemeColors): SystemStyleObject {
  return {
    w: '176px',
    h: '40px',
    px: '16px',
    borderRadius: 0,
    border: 'none',
    bg: 'transparent',
    color: colors.inactiveText,
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: '22px',
    fontFamily: '\'Space Grotesk\', var(--chakra-fonts-body)',
    _selected: {
      bg: colors.activeBg,
      color: colors.activeText,
      _hover: { bg: colors.activeBg, color: colors.activeText },
    },
    _hover: { bg: 'transparent', color: colors.primaryText },
    '&:hover span': { color: 'inherit' },
  };
}

function getDefaultTabSx(colors: TabThemeColors): SystemStyleObject {
  return {
    h: '40px',
    px: '16px',
    borderRadius: '4px',
    border: '1px solid',
    borderColor: colors.tabBorder,
    bg: colors.inactiveBg,
    color: colors.inactiveText,
    fontSize: '14px',
    fontWeight: 500,
    fontFamily: '\'Space Grotesk\', var(--chakra-fonts-body)',
    _selected: {
      bg: colors.activeBg,
      color: colors.activeText,
      borderColor: 'transparent',
      _hover: { bg: colors.activeBg, color: colors.activeText },
    },
    _hover: { bg: colors.inactiveBg, color: colors.primaryText },
    '&:hover span': { color: 'inherit' },
  };
}

function getMenuItemStyles(tabsCut: number, tabsLength: number): StyleProps {
  if (tabsCut < tabsLength) {
    return hiddenItemStyles;
  }

  return { opacity: tabsCut === 0 ? 0 : 1 };
}

interface TabListItemProps {
  tab: TabItem | MenuButton;
  index: number;
  tabsCut: number;
  tabsRefs: Array<React.RefObject<HTMLButtonElement>>;
  isLoading?: boolean;
  activeTabIndex: number;
  tabs: Array<TabItem>;
  onItemClick: (index: number) => void;
  themeProps: ThemingProps<'Tabs'>;
  tabSx: SystemStyleObject;
}

const TabListItem = ({
  tab,
  index,
  tabsCut,
  tabsRefs,
  isLoading,
  activeTabIndex,
  tabs,
  onItemClick,
  themeProps,
  tabSx,
}: TabListItemProps) => {
  if (!tab.id) {
    if (isLoading) {
      return null;
    }

    return (
      <TabsMenu
        key="menu"
        tabs={ tabs }
        activeTab={ tabs[activeTabIndex] }
        tabsCut={ tabsCut }
        isActive={ activeTabIndex >= tabsCut }
        styles={ getMenuItemStyles(tabsCut, tabs.length) }
        onItemClick={ onItemClick }
        buttonRef={ tabsRefs[index] }
        size={ themeProps.size || 'md' }
      />
    );
  }

  return (
    <Tab
      key={ tab.id.toString() }
      ref={ tabsRefs[index] }
      { ...(index < tabsCut ? {} : hiddenItemStyles) }
      scrollSnapAlign="start"
      flexShrink={ 0 }
      sx={ tabSx }
      { ...(index === activeTabIndex ? { 'data-selected': true } : {}) }
    >
      <Skeleton isLoaded={ !isLoading }>
        { typeof tab.title === 'function' ? tab.title() : tab.title }
        <TabCounter count={ tab.count }/>
      </Skeleton>
    </Tab>
  );
};

interface DesktopTabsListProps {
  props: Props;
  tabsList: Array<TabItem | MenuButton>;
  tabsCut: number;
  tabsRefs: Array<React.RefObject<HTMLButtonElement>>;
  listRef: React.RefObject<HTMLDivElement>;
  leftSlotRef: React.RefObject<HTMLDivElement>;
  rightSlotRef: React.RefObject<HTMLDivElement>;
  stickyProps: StyleProps;
  resolvedTabListProps: ChakraProps | undefined;
  segmentedTabListProps: ChakraProps;
  tabSx: SystemStyleObject;
  isSegmented: boolean;
  listBgColor: string;
}

const DesktopTabsList = ({
  props,
  tabsList,
  tabsCut,
  tabsRefs,
  listRef,
  leftSlotRef,
  rightSlotRef,
  stickyProps,
  resolvedTabListProps,
  segmentedTabListProps,
  tabSx,
  isSegmented,
  listBgColor,
}: DesktopTabsListProps) => {
  const visibleTabs = tabsList.slice(0, props.isLoading ? 5 : Infinity);

  return (
    <TabList
      marginBottom={ isSegmented ? 0 : '8px' }
      mx={ isSegmented ? 0 : { base: '-12px', lg: 'unset' } }
      px={ isSegmented ? 0 : { base: '12px', lg: 'unset' } }
      flexWrap="nowrap"
      alignItems="center"
      whiteSpace="nowrap"
      gap={ isSegmented ? 0 : '8px' }
      ref={ listRef }
      overflowX={{ base: 'auto', lg: 'initial' }}
      overscrollBehaviorX="contain"
      css={{
        'scroll-snap-type': 'x mandatory',
        'scroll-padding-inline': '12px',
        '&::-webkit-scrollbar': {
          display: 'none',
        },
        '-ms-overflow-style': 'none',
        'scrollbar-width': 'none',
      }}
      bgColor={ isSegmented ? 'transparent' : listBgColor }
      transitionProperty="top,box-shadow,background-color,color"
      transitionDuration="normal"
      transitionTimingFunction="ease"
      { ...stickyProps }
      { ...segmentedTabListProps }
      { ...resolvedTabListProps }
    >
      { props.leftSlot && <Box ref={ leftSlotRef } { ...props.leftSlotProps }> { props.leftSlot } </Box> }
      { visibleTabs.map((tab, index) => (
        <TabListItem
          key={ tab.id?.toString() ?? 'menu' }
          tab={ tab }
          index={ index }
          tabsCut={ tabsCut }
          tabsRefs={ tabsRefs }
          isLoading={ props.isLoading }
          activeTabIndex={ props.activeTabIndex }
          tabs={ props.tabs }
          onItemClick={ props.onItemClick }
          themeProps={ props.themeProps }
          tabSx={ tabSx }
        />
      )) }
      { props.rightSlot && tabsCut > 0 ?
        <Box ref={ rightSlotRef } ml="auto" { ...props.rightSlotProps }> { props.rightSlot } </Box> :
        null }
    </TabList>
  );
};

interface MobileTabsListProps {
  props: Props;
  listRef: React.RefObject<HTMLDivElement>;
  leftSlotRef: React.RefObject<HTMLDivElement>;
  rightSlotRef: React.RefObject<HTMLDivElement>;
  stickyProps: StyleProps;
  resolvedTabListProps: ChakraProps | undefined;
  listBgColor: string;
}

const MobileTabsList = ({
  props,
  listRef,
  leftSlotRef,
  rightSlotRef,
  stickyProps,
  resolvedTabListProps,
  listBgColor,
}: MobileTabsListProps) => (
  <Flex
    marginBottom="8px"
    mx={{ base: '-12px', lg: 'unset' }}
    px={{ base: '12px', lg: 'unset' }}
    alignItems="flex-start"
    gap="8px"
    flexWrap="wrap"
    ref={ listRef }
    bgColor={ listBgColor }
    transitionProperty="top,box-shadow,background-color,color"
    transitionDuration="normal"
    transitionTimingFunction="ease"
    { ...stickyProps }
    { ...resolvedTabListProps }
  >
    { props.leftSlot && (
      <Box ref={ leftSlotRef } flexShrink={ 0 } { ...props.leftSlotProps }>
        { props.leftSlot }
      </Box>
    ) }
    <Flex flex="1" minW={ 0 } alignItems="center">
      <AdaptiveTabsMobile
        tabs={ props.tabs }
        activeTabIndex={ props.activeTabIndex }
        onItemClick={ props.onItemClick }
        isLoading={ props.isLoading }
      />
    </Flex>
    { props.rightSlot ?
      <Box ref={ rightSlotRef } ml="auto" flexShrink={ 0 } { ...props.rightSlotProps }>{ props.rightSlot }</Box> :
      null }
  </Flex>
);

const AdaptiveTabsList = (props: Props) => {
  const scrollDirection = useScrollDirection();
  const listBgColor = useColorModeValue('#FBFCFE', '#171717');
  const isMobile = useIsMobile();
  const isSegmented = Boolean(props.segmented);

  const tabThemeColors: TabThemeColors = {
    activeBg: '#14DCAC',
    activeText: '#000000',
    inactiveBg: useColorModeValue('#FFFFFF', '#111111'),
    inactiveText: useColorModeValue('#616161', '#999999'),
    primaryText: useColorModeValue('#000000', '#FFFFFF'),
    tabBorder: useColorModeValue('#E9ECEF', '#222222'),
  };
  const separatorColor = useColorModeValue('#E9ECEF', '#222222');

  const tabsList = React.useMemo(() => {
    if (isSegmented) {
      return props.tabs;
    }

    return [ ...props.tabs, menuButton ];
  }, [ isSegmented, props.tabs ]);

  const { tabsCut, tabsRefs, listRef, rightSlotRef, leftSlotRef } = useAdaptiveTabs(tabsList, isMobile || isSegmented);
  const isSticky = useIsSticky(listRef, 5, props.stickyEnabled);
  useScrollToActiveTab({ activeTabIndex: props.activeTabIndex, listRef, tabsRefs, isMobile, isLoading: props.isLoading });

  const stickyProps = getStickyProps(props.stickyEnabled, isSticky, scrollDirection);
  const resolvedTabListProps = resolveTabListProps(props.tabListProps, isSticky, props.activeTabIndex);
  const segmentedTabListProps = isSegmented ? getSegmentedTabListProps(tabThemeColors) : {};
  const tabSx = isSegmented ? getSegmentedTabSx(tabThemeColors) : getDefaultTabSx(tabThemeColors);

  const listProps = {
    props,
    listRef,
    leftSlotRef,
    rightSlotRef,
    stickyProps,
    resolvedTabListProps,
    listBgColor,
  };

  return (
    <>
      { isMobile ? (
        <MobileTabsList { ...listProps }/>
      ) : (
        <DesktopTabsList
          { ...listProps }
          tabsList={ tabsList }
          tabsCut={ tabsCut }
          tabsRefs={ tabsRefs }
          segmentedTabListProps={ segmentedTabListProps }
          tabSx={ tabSx }
          isSegmented={ isSegmented }
        />
      ) }
      { props.separatorEnabled !== false && <Box height="1px" bgColor={ separatorColor }/> }
    </>
  );
};

export default React.memo(AdaptiveTabsList);
